import { ApolloCache } from '@apollo/client'
import { produce } from 'immer'
import { Project, Survey, Wave } from '../../../data/gql-gen/dashboard/graphql'
import { SearchProjects } from '../../../modules/Project/Project.query'

/**
 * Renames a market (Survey) in the cached project so every reader of the name
 * updates without a refetch — market tabs, the project page's markets table,
 * the Confirm/Fieldwork rows and the projects list.
 *
 * Surveys are embedded in the Project (no keyFields, see apolloClient config),
 * so the entry is patched in place — `name` only, leaving `createdDate`/`state`
 * untouched so master-survey selection (surveysByCreation) can't be perturbed.
 * Idempotent (a no-op when the name already matches or the survey is absent)
 * and scoped to the given `projectId`. Mirrors `RemoveMarketCacheUpdate`.
 */
export const RenameMarketCacheUpdate = (
  cache: ApolloCache<unknown>,
  projectId: string,
  surveyId: string,
  name: string
): void => {
  const renameSurvey = (surveys: Survey[] = []): Survey[] => {
    const index = surveys.findIndex((survey) => survey.surveyId === surveyId)
    if (index < 0 || surveys[index].name === name) {
      return surveys
    }
    return produce(surveys, (draft) => {
      draft[index].name = name
    })
  }

  cache.modify({
    fields: {
      // Wave.surveys are embedded copies, not references to the project's.
      waves(existing) {
        const waves = existing as Wave[] | undefined
        if (!waves?.length || waves[0].projectId !== projectId) {
          return existing
        }
        return produce(waves, (draft) => {
          for (const wave of draft) {
            wave.surveys = renameSurvey(wave.surveys)
          }
        })
      },
      // projectById (PROJECT_BY_ID — used by the multi-market pages).
      project(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = renameSurvey(draft.surveys)
        })
      },
      // Keyed per surveyId; runs for every cached entry. Entries for this
      // project share its surveys, so patch each (incl. the master entry the
      // Confirm page and the results export read).
      projectBySurveyId(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = renameSurvey(draft.surveys)
        })
      },
      searchProjects(existing) {
        const searchProjects = existing as SearchProjects | undefined
        const index = searchProjects?.projects.findIndex(
          (project) => project.projectId === projectId
        )
        if (!searchProjects || index === undefined || index < 0) {
          return existing
        }
        return produce(searchProjects, (draft) => {
          draft.projects[index].surveys = renameSurvey(
            draft.projects[index].surveys
          )
        })
      }
    }
  })
}
